import { computed, Injectable, signal, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { AppUser, UserRole } from '../models/auth.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly keycloak = inject(KeycloakService);
  
  private readonly sessionState = signal<boolean>(false);
  private readonly currentUserState = signal<AppUser | null>(null);

  readonly isAuthenticated = computed(() => this.sessionState());
  readonly currentUser = this.currentUserState.asReadonly();
  readonly role = computed<UserRole | null>(() => this.currentUser()?.role ?? null);

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    const isLoggedIn = this.keycloak.isLoggedIn();
    this.sessionState.set(isLoggedIn);

    if (isLoggedIn) {
      const profile = await this.keycloak.loadUserProfile();
      const userRoles = this.keycloak.getUserRoles(false, environment.keycloak.clientId);
      
      const role: UserRole = userRoles.some(r => r.toUpperCase() === 'ADMIN') ? 'ADMIN' : 'RESPONDENTE';

      const formattedName = this.formatNameFromEmail(profile.email || profile.username || '');

      this.currentUserState.set({
        id: profile.id || '',
        name: formattedName || ((profile.firstName || '') + ' ' + (profile.lastName || '')).trim() || 'Usuário',
        email: profile.email || '',
        role: role,
      });

    }
  }

  private formatNameFromEmail(email: string): string {
    if (!email) return '';
    const username = email.split('@')[0];
    return username
      .split(/[\._\-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  async logout(): Promise<void> {
    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    const redirectUri = window.location.origin + baseHref;
    await this.keycloak.logout(redirectUri);
    this.sessionState.set(false);
    this.currentUserState.set(null);
  }
}
